/**
 * Modelo canónico de elementos estáticos del timeline (versión 1) — texto y
 * stickers. Espejo exacto de `backend/app/render/overlay_model.py` y de
 * `services/ffmpeg-renderer/app/overlay_model.py`: mismos nombres, unidades y
 * defaults, para que el preview, el proyecto guardado, el manifiesto y FFmpeg
 * interpreten LO MISMO.
 *
 * Todo es relativo al lienzo:
 *   x_pct/y_pct     centro del elemento (0..1)
 *   scale           fracción del ANCHO (stickers)
 *   font_size_pct   fracción del ALTO (texto)
 *   max_width_pct   fracción del ANCHO (caja de texto)
 *   outline/shadow/box en fracciones del tamaño de fuente
 *
 * Preparado para subtítulos sin rehacer nada: `track_id` admite 'subtitles',
 * cada elemento tiene id estable, tiempos precisos y estilo reutilizable.
 */
export const ELEMENT_MODEL_VERSION = 1;
export const ELEMENT_SCHEMA_VERSION = 1;

export const ELEMENT_TYPES = ['text', 'sticker'];
export const ELEMENT_TRACK_IDS = ['text', 'sticker', 'subtitles'];

export const FONT_LIBRARY = {
  inter: { label: 'Inter', css: 'SMLInter', file: 'Inter-Variable.ttf' },
  montserrat: { label: 'Montserrat', css: 'SMLMontserrat', file: 'Montserrat-Variable.ttf' },
  bebas: { label: 'Bebas Neue', css: 'SMLBebas', file: 'BebasNeue-Regular.ttf' },
  playfair: { label: 'Playfair Display', css: 'SMLPlayfair', file: 'PlayfairDisplay-Variable.ttf' },
};
export const DEFAULT_FONT = 'inter';

export const TRANSFORM_DEFAULTS = {
  x_pct: 0.5, y_pct: 0.5, scale: 0.3, rotation: 0, opacity: 1,
};

export const SHADOW_DEFAULTS = {
  enabled: false, color: '#000000', opacity: 0.55,
  blur_pct: 0.12, offset_x_pct: 0.04, offset_y_pct: 0.06,
};
export const OUTLINE_DEFAULTS = { enabled: false, color: '#000000', width_pct: 0.08 };
export const BOX_DEFAULTS = {
  enabled: false, color: '#000000', opacity: 0.55, padding_pct: 0.28, radius_pct: 0.22,
};

export const TEXT_STYLE_DEFAULTS = {
  content: 'Texto',
  font_family: DEFAULT_FONT,
  font_size_pct: 0.075,
  font_weight: 700,
  color: '#ffffff',
  align: 'center',
  line_height: 1.18,
  letter_spacing_pct: 0,
  max_width_pct: 0.8,
  uppercase: false,
  preset: 'custom',
};

/** Presets = estilos visuales de texto. No son subtítulos automáticos. */
export const TEXT_PRESETS = {
  title: {
    label: 'Título',
    style: {
      font_family: 'montserrat', font_size_pct: 0.11, font_weight: 800,
      color: '#ffffff', align: 'center', max_width_pct: 0.86,
      uppercase: true, letter_spacing_pct: 0.02,
      shadow: { enabled: true, opacity: 0.6, blur_pct: 0.14, offset_x_pct: 0, offset_y_pct: 0.06 },
    },
    transform: { y_pct: 0.22 },
  },
  subtitle: {
    label: 'Subtítulo visual',
    style: {
      font_family: 'inter', font_size_pct: 0.055, font_weight: 600,
      color: '#ffffff', align: 'center', max_width_pct: 0.78,
      outline: { enabled: true, color: '#000000', width_pct: 0.07 },
      box: { enabled: true, color: '#000000', opacity: 0.5, padding_pct: 0.3, radius_pct: 0.2 },
    },
    transform: { y_pct: 0.84 },
  },
  cta: {
    label: 'Llamada a la acción',
    style: {
      font_family: 'montserrat', font_size_pct: 0.062, font_weight: 800,
      color: '#12121a', align: 'center', max_width_pct: 0.7,
      uppercase: true, letter_spacing_pct: 0.03,
      box: { enabled: true, color: '#fbbf24', opacity: 0.95, padding_pct: 0.38, radius_pct: 0.5 },
    },
    transform: { y_pct: 0.76 },
  },
  highlight: {
    label: 'Texto destacado',
    style: {
      font_family: 'bebas', font_size_pct: 0.14, font_weight: 400,
      color: '#fbbf24', align: 'center', max_width_pct: 0.9, uppercase: true,
      outline: { enabled: true, color: '#12121a', width_pct: 0.1 },
      shadow: { enabled: true, opacity: 0.5, blur_pct: 0.18 },
    },
    transform: { y_pct: 0.5 },
  },
};

const num = (value, fallback, low, high) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return fallback;
  return Math.max(low, Math.min(high, n));
};
const bool = (value, fallback) => (value == null ? fallback : !!value);
const color = (value, fallback) =>
  (typeof value === 'string' && /^#[0-9a-fA-F]{6}$/.test(value.trim())
    ? value.trim().toLowerCase() : fallback);

export const normalizeTransform = (raw = {}) => ({
  x_pct: num(raw?.x_pct, TRANSFORM_DEFAULTS.x_pct, -1, 2),
  y_pct: num(raw?.y_pct, TRANSFORM_DEFAULTS.y_pct, -1, 2),
  scale: num(raw?.scale, TRANSFORM_DEFAULTS.scale, 0.02, 3),
  rotation: num(raw?.rotation, 0, -360, 360),
  opacity: num(raw?.opacity, 1, 0, 1),
});

const normalizeShadow = (raw = {}) => ({
  enabled: bool(raw?.enabled, SHADOW_DEFAULTS.enabled),
  color: color(raw?.color, SHADOW_DEFAULTS.color),
  opacity: num(raw?.opacity, SHADOW_DEFAULTS.opacity, 0, 1),
  blur_pct: num(raw?.blur_pct, SHADOW_DEFAULTS.blur_pct, 0, 1),
  offset_x_pct: num(raw?.offset_x_pct, SHADOW_DEFAULTS.offset_x_pct, -1, 1),
  offset_y_pct: num(raw?.offset_y_pct, SHADOW_DEFAULTS.offset_y_pct, -1, 1),
});
const normalizeOutline = (raw = {}) => ({
  enabled: bool(raw?.enabled, OUTLINE_DEFAULTS.enabled),
  color: color(raw?.color, OUTLINE_DEFAULTS.color),
  width_pct: num(raw?.width_pct, OUTLINE_DEFAULTS.width_pct, 0, 0.5),
});
const normalizeBox = (raw = {}) => ({
  enabled: bool(raw?.enabled, BOX_DEFAULTS.enabled),
  color: color(raw?.color, BOX_DEFAULTS.color),
  opacity: num(raw?.opacity, BOX_DEFAULTS.opacity, 0, 1),
  padding_pct: num(raw?.padding_pct, BOX_DEFAULTS.padding_pct, 0, 2),
  radius_pct: num(raw?.radius_pct, BOX_DEFAULTS.radius_pct, 0, 1),
});

export const normalizeTextStyle = (raw = {}) => {
  const family = String(raw?.font_family || DEFAULT_FONT);
  const align = String(raw?.align || 'center').toLowerCase();
  const preset = String(raw?.preset || 'custom');
  return {
    content: raw?.content == null ? TEXT_STYLE_DEFAULTS.content : String(raw.content),
    font_family: FONT_LIBRARY[family] ? family : DEFAULT_FONT,
    font_size_pct: num(raw?.font_size_pct, TEXT_STYLE_DEFAULTS.font_size_pct, 0.01, 0.5),
    font_weight: Math.round(num(raw?.font_weight, 700, 100, 900)),
    color: color(raw?.color, TEXT_STYLE_DEFAULTS.color),
    align: ['left', 'center', 'right'].includes(align) ? align : 'center',
    line_height: num(raw?.line_height, TEXT_STYLE_DEFAULTS.line_height, 0.8, 3),
    letter_spacing_pct: num(raw?.letter_spacing_pct, 0, -0.2, 0.5),
    max_width_pct: num(raw?.max_width_pct, TEXT_STYLE_DEFAULTS.max_width_pct, 0.1, 1),
    uppercase: bool(raw?.uppercase, false),
    preset: TEXT_PRESETS[preset] || preset === 'custom' ? preset : 'custom',
    shadow: normalizeShadow(raw?.shadow),
    outline: normalizeOutline(raw?.outline),
    box: normalizeBox(raw?.box),
  };
};

export const normalizeElement = (raw = {}, index = 0) => {
  const type = ELEMENT_TYPES.includes(raw?.type) ? raw.type : 'text';
  const trackId = ELEMENT_TRACK_IDS.includes(raw?.track_id) ? raw.track_id : type;
  return {
    element_id: String(raw?.element_id || raw?.id || `el_${index}`),
    type,
    track_id: trackId,
    asset_id: raw?.asset_id || null,
    source_url: raw?.source_url || null,
    start: num(raw?.start ?? raw?.start_time, 0, 0, 86400),
    duration: num(raw?.duration, 3, 0.05, 86400),
    fade_in: num(raw?.fade_in, 0, 0, 86400),
    fade_out: num(raw?.fade_out, 0, 0, 86400),
    transform: normalizeTransform(raw?.transform),
    style: type === 'text' ? normalizeTextStyle(raw?.style) : {},
    z_order: Math.round(num(raw?.z_order, index, -999, 999)),
    visible: bool(raw?.visible, true),
    label: raw?.label || null,
    schema_version: Math.round(num(raw?.schema_version, ELEMENT_SCHEMA_VERSION, 1, 99)),
  };
};

export const normalizeElements = (raw) =>
  (Array.isArray(raw) ? raw : []).map((item, index) => normalizeElement(item, index))
    .sort((a, b) => (a.z_order - b.z_order) || a.element_id.localeCompare(b.element_id));

export const applyPreset = (name) => {
  const preset = TEXT_PRESETS[name];
  if (!preset) {
    return { style: normalizeTextStyle({}), transform: normalizeTransform({}) };
  }
  return {
    style: normalizeTextStyle({ ...preset.style, preset: name }),
    transform: normalizeTransform({ ...TRANSFORM_DEFAULTS, ...(preset.transform || {}) }),
  };
};

export const isElementActive = (element, timeS) =>
  !!element?.visible && timeS >= element.start && timeS < element.start + element.duration;

/** Estilos CSS del preview derivados del MISMO modelo que usa el renderer. */
export const textPreviewStyles = (element, canvasHeight, canvasWidth) => {
  const style = normalizeTextStyle(element?.style);
  const transform = normalizeTransform(element?.transform);
  const fontPx = style.font_size_pct * canvasHeight;
  const shadow = style.shadow;
  const outline = style.outline;
  const box = style.box;
  const hexA = (hex, alpha) => {
    const v = hex.replace('#', '');
    const [r, g, b] = [0, 2, 4].map((i) => parseInt(v.slice(i, i + 2), 16));
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  return {
    wrapper: {
      position: 'absolute',
      left: `${transform.x_pct * 100}%`,
      top: `${transform.y_pct * 100}%`,
      width: `${style.max_width_pct * 100}%`,
      transform: `translate(-50%, -50%) rotate(${transform.rotation}deg)`,
      opacity: transform.opacity,
      textAlign: style.align,
      pointerEvents: 'auto',
    },
    text: {
      display: 'inline-block',
      margin: 0,
      fontFamily: `'${FONT_LIBRARY[style.font_family].css}', system-ui, sans-serif`,
      fontSize: `${fontPx}px`,
      fontWeight: style.font_weight,
      lineHeight: style.line_height,
      letterSpacing: `${style.letter_spacing_pct * fontPx}px`,
      color: style.color,
      whiteSpace: 'pre-wrap',
      textTransform: style.uppercase ? 'uppercase' : 'none',
      textShadow: shadow.enabled
        ? `${shadow.offset_x_pct * fontPx}px ${shadow.offset_y_pct * fontPx}px ${shadow.blur_pct * fontPx}px ${hexA(shadow.color, shadow.opacity)}`
        : 'none',
      WebkitTextStrokeWidth: outline.enabled ? `${outline.width_pct * fontPx}px` : '0',
      WebkitTextStrokeColor: outline.color,
      paintOrder: 'stroke fill',
      background: box.enabled ? hexA(box.color, box.opacity) : 'transparent',
      padding: box.enabled
        ? `${box.padding_pct * fontPx * 0.55}px ${box.padding_pct * fontPx}px`
        : 0,
      borderRadius: box.enabled
        ? `${box.radius_pct * (style.line_height * fontPx)}px`
        : 0,
    },
  };
};

export const stickerPreviewStyles = (element) => {
  const transform = normalizeTransform(element?.transform);
  return {
    position: 'absolute',
    left: `${transform.x_pct * 100}%`,
    top: `${transform.y_pct * 100}%`,
    width: `${transform.scale * 100}%`,
    transform: `translate(-50%, -50%) rotate(${transform.rotation}deg)`,
    opacity: transform.opacity,
  };
};
